import { Fragment, createContext, useContext } from "react"

import { merge, unescape } from "lodash"
import { renderToStaticMarkup } from "react-dom/server.browser"

export default class TemplateEngine {
  constructor() {
    this.Templates = {}
  }

  defineComponent({ Template, key }) {
    this.Templates[key] = Template
  }

  buildTheme(definition, stores) {
    return new ThemeBuilder(definition, stores, block => this.Templates[block] || Fragment)
  }

  buildHTML(craft) {
    const theme = new ThemeBuilder(null, null, block => this.Templates[block] || Fragment)
    return theme.compileHTML(craft)
  }
}

class ThemeBuilder {
  constructor(definition, store, getComponent) {
    this.definition = definition
    this.getComponent = getComponent
    this.init = () => this.register(store)
  }

  registerTemplate = name => {
    if (!this.registry) return null;
    return this.registry.template(name)
  }

  registerLayout = name => {
    if (!this.registry) return null;
    return this.registry.layout(name)
  }

  registerPartial = name => {
    if (!this.registry) return null;
    return this.registry.partial(name)
  }

  register = store => {
    const resolver = type => name => {
      const { [type]: { items } } = store
      const { definition: { craft } } = items.find(({ name: key }) => key === name)
      return craft
    }

    this.registry = {
      files: [],
      template: this.processor('./', resolver('templates'), {}),
      layout: this.processor('./layouts/', resolver('layouts'), {}),
      partial: this.processor('./partials/', resolver('partials'), {}),
    }
  }

  compileHTML = craft => {
    const blocks = this.parse(craft).blocks('ROOT')
    const html = this.compile([blocks])
    return { html, blocks }
  }

  parse(data) {
    return {
      blocks(key) {
        const {
          props,
          type,
          type: { resolvedName },
          linkedNodes,
          nodes,
          /* displayName, custom, isCanvas, hidden, parent */
        } = data[key]

        /*
        if (nodes.length && Object.values(linkedNodes).length) {
          console.error('Não pode ter nodes e linkedNodes ao mesmo tempo!?')
        }
        */
        const blocks = nodes.length ? nodes : Object.values(linkedNodes)

        return {
          key,
          block: resolvedName || type,
          scope: props,
          blocks: blocks.map(key => this.blocks(key)),
        }
      },
    }
  }

  compileBlocks(block, blocks, { properties, ...props }) {
    const TemplateComponent = this.getComponent(block)

    props = merge({}, properties, props)

    if (!blocks || !blocks.length) {
      return <TemplateComponent {...props} />
    }

    return <TemplateComponent key={block} {...props}>
      {blocks.map(({ block, blocks, scope }) => this.compileBlocks(block, blocks, scope))}
    </TemplateComponent>
  }

  compile(blocks) {
    const children = <TemplateEngineProvider value={this}>
      {this.compileBlocks(null, blocks, {})}
    </TemplateEngineProvider>

    const html = new DOMParser().parseFromString(renderToStaticMarkup(children), 'text/html').body.innerHTML
    return unescape(html)
  }

  buildFiles() {
    const { types: { list: types } } = this.definition

    this.init()
    for (const { templates } of types) {
      for (const { name, definition: { craft } } of templates) {
        this.registry.template(name, craft)
      }
    }
    return this.registry.files
  }

  processor = (path, resolver, registered) => (name, craft) => {
    if (!registered[name]) {
      registered[name] = true
      const { html } = this.compileHTML(craft || resolver(name))

      this.registry.files.push({
        path: `${path}${name}.hbs`,
        html,
      })
    }
  }
}

/**
 * @type {ThemeBuilder}
*/
const INITIAL = {}
const context = createContext(INITIAL)

export const TemplateEngineProvider = context.Provider
export const useTemplateEngineContext = () => useContext(context)
